import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, MessageCircle, Send, Trash2, X, Briefcase, UserRound, Layers, CalendarDays, CalendarClock, LayoutGrid, GalleryHorizontalEnd, SquareCheckBig, CalendarCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useIsMobile, BottomSheet } from "@/components/shared/PagePrimitives";
import { ListHeader, ListToolbar } from "@/components/shared/ListToolbar";
import { InfoTip } from "@/components/settings/Grouped";
import { PhotoPinViewer, photoInfo } from "@/components/defects/PhotoPinViewer";
import { useListFilters } from "@/hooks/useListFilters";
import { useAuth, canLeadTasks } from "@/context/AuthContext";
import { DefectTaskForm } from "@/components/defects/DefectTaskForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDate, plur } from "@/lib/format";
import { GalleryGrid, canRedate } from "@/components/gallery/GalleryGrid";
import { GalleryViewer } from "@/components/gallery/GalleryViewer";
import { GalleryDetailPanel } from "@/components/gallery/GalleryDetailPanel";
import { RedatePanel } from "@/components/gallery/RedatePanel";
import { GalleryUploadDialog } from "@/components/gallery/GalleryUploadDialog";
import { ShareSheet, createShareUrl, shareText, shareToWhatsApp } from "@/components/gallery/ShareSheet";
import { LoadMore } from "@/components/shared/LoadMore";
import { ask } from "@/lib/confirm";

const EYE_ROLES = ["OWNER", "MANAGER", "ASSISTANT"];
const DEL_ROLES = ["OWNER", "ASSISTANT"];
// 216: galerie po stránkách. Filtry (zdroj, autor, zakázka, hledání, Upravitelné) počítá server.
// 347: jednotná lišta — Dnes / Vše, Filtr (Zakázka / Autor / Zdroj / Den / Datum fotky, víc hodnot),
// pohledy Mřížka / Prohlížeč vpravo, Vybrat a Změnit datum v ⋯.
const PAGE = 60;
const EMPTY = { photos: [], sources: [], authors: [], projects: [], total: 0,
  edit_counts: { all: 0, upload: 0, locked: 0 }, next_cursor: null };

export default function Gallery() {
  const { user } = useAuth();
  const canEye = EYE_ROLES.includes(user?.role);
  const canDelete = DEL_ROLES.includes(user?.role);
  const canSelect = canEye;
  const mobile = useIsMobile();

  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");
  const [searchQ, setSearchQ] = useState("");        // hledání pro server (po pauze v psaní)
  const seenRef = useRef(new Map());                 // id → fotka: výběr a detail přežijí změnu filtru
  const reqRef = useRef(0);                          // novější dotaz zahodí odpověď staršího
  const loadedRef = useRef(0);
  const [view, setView] = useState("grid");
  const [viewerIndex, setViewerIndex] = useState(0);
  const [mode, setMode] = useState(null); // null | "select" (pracovní) | "redate" (změna data)
  const [selected, setSelected] = useState(new Set());
  const [redateDate, setRedateDate] = useState("");
  const [dayF, setDayF] = useState("today"); // "today" | "all" | "custom" — nepersistováno
  const [detailId, setDetailId] = useState(null);
  const [lightboxId, setLightboxId] = useState(null);
  const [share, setShare] = useState(null);
  const [drafts, setDrafts] = useState([]);          // 187: košík závad z galerie
  const [defTask, setDefTask] = useState(null);      // { projectId, projectName, assignee, returnTask }
  const [focusDraft, setFocusDraft] = useState(null);
  const [bulkDelete, setBulkDelete] = useState(false);
  const [upload, setUpload] = useState(() => new URLSearchParams(window.location.search).get("nahrat") === "1");   // 236: rychlá akce na úvodu
  const [dragCount, setDragCount] = useState(0);
  const redateRef = useRef(null);

  useEffect(() => { const t = setTimeout(() => setSearchQ(search.trim()), 300); return () => clearTimeout(t); }, [search]);

  const known = useRef({ project: new Map(), author: new Map(), source: new Map() });   // popisky vybraných hodnot mimo dnešní nabídku
  const fGroups = useMemo(() => {
    const k = known.current, projects = data.projects || [], ec = data.edit_counts || EMPTY.edit_counts;
    projects.forEach((x) => k.project.set(x.id, x.name));
    data.authors.forEach((x) => k.author.set(x.id, x.name));
    data.sources.forEach((x) => k.source.set(x.value, x.label));
    return [
      { key: "project", label: "Zakázka", icon: Briefcase, noNeg: true, labelOf: (v) => k.project.get(v),
        options: projects.map((x) => ({ value: x.id, label: x.name })), counts: Object.fromEntries(projects.map((x) => [x.id, x.count])) },
      { key: "author", label: "Autor", icon: UserRound, noNeg: true, labelOf: (v) => k.author.get(v),
        options: data.authors.map((a) => ({ value: a.id, label: a.name })) },
      { key: "source", label: "Zdroj", icon: Layers, noNeg: true, labelOf: (v) => k.source.get(v),
        options: data.sources.map((x) => ({ value: x.value, label: x.label })), counts: Object.fromEntries(data.sources.map((x) => [x.value, x.count])) },
      { key: "den", label: "Den", icon: CalendarDays, type: "date", format: formatDate },
      { key: "datum", label: "Datum fotky", icon: CalendarClock, noNeg: true, counts: { upload: ec.upload, locked: ec.locked },
        options: [{ value: "upload", label: "Upravitelné" }, { value: "locked", label: "Pevné (docházka, výkaz, úkol)" }] },
    ];
  }, [data]);
  const F = useListFilters(null, { groups: fGroups, entity: { one: "fotka", few: "fotky", many: "fotek" } });
  const st = F.state, fKey = JSON.stringify(st);
  const editable = st.datum?.vals.length === 1 ? st.datum.vals[0] : undefined;   // obě hodnoty = vše
  const effDate = st.den ? st.den.vals[0] : dayF === "today" ? new Date().toISOString().slice(0, 10) : undefined;
  const query = useMemo(() => ({
    date: effDate,
    source: st.source?.vals.join(","),
    author_id: st.author?.vals.join(","),
    project_id: st.project?.vals.join(","),
    q: searchQ || undefined,
    editable,
  }), [fKey, effDate, searchQ]); // eslint-disable-line react-hooks/exhaustive-deps

  const remember = (list) => list.forEach((p) => seenRef.current.set(p.id, p));
  // `keep` = kolik fotek už je načteno — obnova po akci (smazání, změna data,
  // nahrání) vrátí stejně dlouhý seznam, ať mřížka neuskočí.
  const load = useCallback(async (keep = 0) => {
    const id = ++reqRef.current;
    const r = await api.get("/photos", { params: { ...query, limit: Math.min(500, Math.max(PAGE, keep)) } });
    if (id !== reqRef.current) return;
    remember(r.data.photos);
    setData(r.data);
  }, [query]);
  const refresh = () => load(loadedRef.current);
  loadedRef.current = data.photos.length;

  useEffect(() => { (async () => { try { await load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); } })(); }, [load]);

  const loadMore = useCallback(async () => {
    if (!data.next_cursor || loadingMore) return;
    const id = reqRef.current;
    setLoadingMore(true);
    try {
      const r = await api.get("/photos", { params: { ...query, limit: PAGE, cursor: data.next_cursor } });
      if (id !== reqRef.current) return;
      remember(r.data.photos);
      setData((cur) => {
        const have = new Set(cur.photos.map((p) => p.id));
        return { ...r.data, photos: [...cur.photos, ...r.data.photos.filter((p) => !have.has(p.id))] };
      });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoadingMore(false); }
  }, [data.next_cursor, loadingMore, query]);

  // Datum fotky filtruje server; po změně data (redate) dočistíme hned i načtené.
  const photos = editable === "upload" ? data.photos.filter(canRedate)
    : editable === "locked" ? data.photos.filter((p) => !canRedate(p)) : data.photos;
  const byId = (id) => data.photos.find((p) => p.id === id) || seenRef.current.get(id) || null;
  const byUrl = (url) => photos.find((p) => p.url === url) || data.photos.find((p) => p.url === url)
    || [...seenRef.current.values()].find((p) => p.url === url) || null;

  // Detail i lightbox drží jen id — objekt se odvozuje z čerstvých dat, takže
  // po přepnutí oka panel i lightbox okamžitě ukazují nový stav (zadání 138 A).
  const detail = useMemo(() => photos.find((p) => p.id === detailId) || (detailId ? byId(detailId) : null), [photos, data.photos, detailId]); // eslint-disable-line react-hooks/exhaustive-deps
  const lightbox = useMemo(() => photos.find((p) => p.id === lightboxId) || (lightboxId ? byId(lightboxId) : null), [photos, data.photos, lightboxId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Limit 500 fotek na akci — stejný strop má redate, approve i hromadné mazání.
  const toggleSelect = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id);
    else {
      if (n.size >= 500) { toast.error("Označit jde nejvýš 500 fotek najednou."); return s; }
      n.add(id);
    }
    return n;
  });
  const clearSelect = () => { setSelected(new Set()); setMode(null); setRedateDate(""); };
  // Režimy jsou výhradní: spuštění jednoho ukončí druhý a smaže výběr.
  const startMode = (m) => { setSelected(new Set()); setRedateDate(""); setMode((cur) => (cur === m ? null : m)); };

  // Tap na tužku ve štítku: spustí režim Změnit datum s předvybranou fotkou.
  const onRedateTap = (p) => {
    setMode("redate");
    setSelected((s) => { const n = mode === "redate" ? new Set(s) : new Set(); n.add(p.id); return n; });
    setTimeout(() => redateRef.current?.scrollIntoView({ block: "nearest" }), 60);
  };
  const selectedPhotos = [...selected].map(byId).filter(Boolean);
  // Výběr přežije změnu filtrů — počítáme, kolik vybraných teď není vidět.
  const hiddenSelected = selected.size - photos.filter((p) => selected.has(p.id)).length;

  const toggleEye = async (ids, approved, dateLbl) => {
    // Optimistický flip — oko i tlačítko se přepnou hned; při chybě se vrátí.
    // 216: bez znovunačtení celé galerie — server zapíše totéž, co tady doplníme.
    const ts = new Date().toISOString();
    const mark = (p, st) => (st === "approved"
      ? { ...p, status: st, approved_at: ts, approved_by_name: user?.name }
      : { ...p, status: st, approved_at: null, approved_by_name: null });
    const patch = (st) => {
      ids.forEach((id) => { const p = seenRef.current.get(id); if (p) seenRef.current.set(id, mark(p, st)); });
      setData((d) => ({ ...d, photos: d.photos.map((p) => (ids.includes(p.id) ? mark(p, st) : p)) }));
    };
    patch(approved ? "approved" : "pending");
    try {
      await api.post("/diary/photos/approve", { photo_ids: ids, approved });
      const n = ids.length;
      if (approved) toast.success(`✓ ${plur(n, "fotka přidána", "fotky přidány", "fotek přidáno")} do deníku${dateLbl ? ` — ${dateLbl}` : ""}. Uvidí je investor.`);
      else toast.success(`${plur(n, "fotka odebrána", "fotky odebrány", "fotek odebráno")} z deníku — investor je už neuvidí.`);
    } catch (e) {
      patch(approved ? "pending" : "approved");
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const eyeOne = (p) => toggleEye([p.id], p.status !== "approved", formatDate(p.date));

  const redate = async (ids, date) => {
    try {
      const r = await api.patch("/photos/date", { ids, date });
      await refresh();
      const { updated, skipped } = r.data;
      let msg = `✓ ${plur(updated, "fotka přepsána", "fotky přepsány", "fotek přepsáno")} na ${formatDate(date)}.`;
      if (skipped) msg += ` ${plur(skipped, "fotka zůstala", "fotky zůstaly", "fotek zůstalo")} — datum z docházky ani úkolu měnit nelze.`;
      toast.success(msg);
      clearSelect();
    } catch (e) {
      if (e.response?.status === 409) toast.error(e.response.data.detail);
      else toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // 187: závady z galerie → úkol. Úkol patří k jedné zakázce; u fotek z úkolu, který
  // čeká na schválení, jde i Vrátit k opravě.
  const openDefTask = async () => {
    const rows = drafts.map((d) => byUrl(d.photo_url)).filter(Boolean);
    const pids = [...new Set(rows.map((p) => p.project_id))];
    if (pids.length !== 1) { toast.error("Závady jsou z více zakázek — úkol udělejte pro každou zakázku zvlášť."); return; }
    setLightboxId(null); setFocusDraft(null);
    const first = rows[0];
    const tids = [...new Set(rows.map((p) => (p.source === "task" ? p.task_id : null)))];
    let returnTask = null;
    if (tids.length === 1 && tids[0]) {
      try { const r = await api.get(`/tasks/${tids[0]}`); if (r.data?.status === "Hotovo") returnTask = { id: r.data.id, title: r.data.title }; } catch { /* jen nabídka */ }
    }
    setDefTask({ projectId: pids[0], projectName: first.project_name, assignee: first.author_id ? { id: first.author_id, name: first.author_name } : null, returnTask });
  };

  const doDelete = async (p) => {
    if (!await ask("Opravdu smazat tuto fotku? Zmizí i z deníku.")) return;
    try { await api.delete(`/photos/${p.id}`); await refresh(); setDetailId(null); toast.success("Fotka smazána."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doBulkDelete = async () => {
    const n = selected.size;
    try {
      await api.post("/photos/delete", { photo_ids: [...selected] });
      await refresh();
      clearSelect();
      setDetailId(null);
      toast.success(`${plur(n, "fotka smazána", "fotky smazány", "fotek smazáno")}.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // WhatsApp z lišty výběru posílá rovnou — okno se otevírá PŘED await, jinak
  // ho prohlížeč zablokuje jako popup; dialog ShareSheet je jen pro „Poslat jinak".
  const shareWhatsApp = async () => {
    const w = window.open("about:blank", "_blank");
    try {
      const u = await createShareUrl([...selected]);
      const wa = `https://wa.me/?text=${encodeURIComponent(shareText(u))}`;
      if (w) w.location = wa; else window.open(wa, "_blank");
    } catch (e) { w?.close(); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Desktop: klik na dlaždici otevře pravý panel; celoobrazovkový lightbox jen
  // přes „Zvětšit" nebo klik na náhled v panelu. Mobil: lightbox = detail.
  const openDetail = (p) => { if (mobile) setLightboxId(p.id); else setDetailId(p.id); };
  const lbIndex = lightbox ? photos.findIndex((x) => x.id === lightbox.id) : -1;
  const bulkApproved = selectedPhotos.some((p) => p.status !== "approved"); // je-li cokoli pending → Zobrazit

  // Klávesy na desktopu: ←/→ procházejí fotky v panelu, Esc opustí režim / zavře panel.
  useEffect(() => {
    if (mobile) return undefined;
    const h = (e) => {
      if (lightboxId) return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key === "Escape") {
        if (mode) clearSelect(); else if (detailId) setDetailId(null);
        return;
      }
      if (!detailId) return;
      const i = photos.findIndex((p) => p.id === detailId);
      if (e.key === "ArrowRight" && i >= 0 && i < photos.length - 1) setDetailId(photos[i + 1].id);
      else if (e.key === "ArrowLeft" && i > 0) setDetailId(photos[i - 1].id);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [mobile, detailId, lightboxId, photos, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prohlížeč a šipky v panelu došly ke konci načtených fotek → další stránka.
  const viewerAt = view === "viewer" ? viewerIndex : detailId ? photos.findIndex((p) => p.id === detailId) : -1;
  useEffect(() => {
    if (viewerAt >= 0 && viewerAt >= photos.length - 3 && data.next_cursor) loadMore();
  }, [viewerAt, photos.length, data.next_cursor]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="text-muted-foreground" data-testid="gallery-loading">Načítám…</div>;

  const detailNode = (
    <GalleryDetailPanel photo={detail} canEye={canEye} canDelete={canDelete}
      onEye={eyeOne} onZoom={(p) => setLightboxId(p.id)} onSend={(p) => setShare([p.id])} onDelete={doDelete}
      onRedateTap={canSelect ? (p) => { if (mobile) setDetailId(null); onRedateTap(p); } : undefined} />
  );

  return (
    <div className="space-y-5 pb-24 lg:pb-0 overflow-x-clip" style={{ paddingBottom: "max(6rem, env(safe-area-inset-bottom))" }} data-testid="gallery-page">
      <ListHeader title="Galerie" mobile={mobile}
        summary={<span className="inline-flex items-center gap-1.5">{plur(data.total, "fotka", "fotky", "fotek")}
          <InfoTip label="Co znamená oko" text="Fotky ze všech zakázek. Oko u fotky = ukázat ji investorovi v deníku." /></span>}
        search={search} onSearch={setSearch} searchPh="Hledat popisek, autora, zakázku…"
        primary={canEye ? { label: "Nahrát", full: "Nahrát fotky", onClick: () => setUpload(true), testid: "gallery-upload-btn" } : null}
        more={canSelect && view === "grid" ? [
          { icon: SquareCheckBig, label: mode === "select" ? "Ukončit výběr" : "Vybrat fotky", onClick: () => startMode("select"), testid: "select-toggle" },
          { icon: CalendarCog, label: mode === "redate" ? "Ukončit změnu data" : "Změnit datum fotek", onClick: () => startMode("redate"), testid: "redate-toggle" },
        ] : []} />

      <ListToolbar mobile={mobile} testid="gallery-toolbar"
        tabs={[{ value: "today", label: "Dnes" }, { value: "all", label: "Vše" }]}
        tab={st.den ? "custom" : dayF} tabLabel={st.den && formatDate(st.den.vals[0])}
        onTab={(v) => { setDayF(v); F.clearOne("den"); }}
        filter={F} search={search} onSearch={setSearch} searchPh="Hledat popisek, autora, zakázku…"
        // Přepnutí do Prohlížeče ruší režimy — hromadné akce patří Mřížce.
        views={[{ value: "grid", label: "Mřížka", icon: LayoutGrid }, { value: "viewer", label: "Prohlížeč", icon: GalleryHorizontalEnd }]}
        view={view} onView={(v) => { setView(v); if (v === "viewer") clearSelect(); }} />

      {mode && view === "grid" && (
        <div className="flex items-center gap-2 flex-wrap rounded-[14px] bg-brand/10 border border-brand/30 px-3 py-2" data-testid="gallery-bulk-bar">
          <span className="text-[11px] font-bold uppercase tracking-wide text-brand" data-testid="bulk-mode-label">{mode === "select" ? "Výběr" : "Změna data"}</span>
          <span className="font-bold text-sm">
            {!selected.size && <span className="font-normal text-muted-foreground" data-testid="bulk-hint">Klepni na fotky{mode === "redate" ? ", kterým chceš změnit datum" : ""}.</span>}
            {selected.size > 0 && plur(selected.size, "vybraná fotka", "vybrané fotky", "vybraných fotek")}
            {hiddenSelected > 0 && <span className="font-normal text-muted-foreground"> · z toho {hiddenSelected} mimo aktuální filtr</span>}
          </span>
          {mode === "select" && selected.size > 0 && (
            <>
              {canEye && (
                <Button size="sm" className="rounded-full" onClick={() => toggleEye([...selected], bulkApproved)} data-testid="bulk-diary">
                  {bulkApproved ? <><Eye size={15} className="mr-1" /> Zobrazit v deníku</> : <><EyeOff size={15} className="mr-1" /> Skrýt z deníku</>}
                </Button>
              )}
              <Button size="sm" variant="outline" className="rounded-full" onClick={shareWhatsApp} data-testid="bulk-whatsapp"><MessageCircle size={15} className="mr-1" /> WhatsApp</Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => setShare([...selected])} data-testid="bulk-share"><Send size={15} className="mr-1" /> Poslat jinak</Button>
              {canDelete && (
                <Button size="sm" variant="outline" className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setBulkDelete(true)} data-testid="bulk-delete">
                  <Trash2 size={15} className="mr-1" /> Smazat ({selected.size})
                </Button>
              )}
            </>
          )}
          <Button size="sm" variant="ghost" className="rounded-full ml-auto" onClick={clearSelect} data-testid="bulk-clear">{selected.size ? <><X size={15} className="mr-1" /> Zrušit výběr</> : "Hotovo"}</Button>
        </div>
      )}

      {mode === "redate" && view === "grid" && (
        <p className="text-sm text-muted-foreground" data-testid="redate-helper">
          Klepni na fotky, vyber nové datum a potvrď. Šedé fotky jsou z docházky, výkazu nebo úkolu — těm datum měnit nejde.
        </p>
      )}

      {view === "viewer" ? (
        photos.length ? <GalleryViewer photos={photos} total={data.total} index={Math.min(viewerIndex, photos.length - 1)} onIndex={setViewerIndex} onClose={() => setView("grid")}
          canEye={canEye} onEye={eyeOne} onSend={(p) => setShare([p.id])} onZoom={(p) => setLightboxId(p.id)} />
          : <div className="p-8 text-center text-muted-foreground text-sm">Žádné fotky.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-5">
          <div className="min-w-0">
            {photos.length ? (
              <>
                <GalleryGrid photos={photos} canEye={canEye} mode={mode} selected={selected}
                  onToggleSelect={toggleSelect} onOpen={openDetail} onEye={eyeOne}
                  onRedateTap={canSelect ? onRedateTap : undefined} onDragState={setDragCount} />
                <LoadMore hasMore={!!data.next_cursor} loading={loadingMore} onMore={loadMore}
                  shown={photos.length} total={data.total} testid="gallery-more" />
              </>
            ) : dayF === "today" && !st.den ? (
              <div className="p-8 text-center text-sm space-y-1" data-testid="gallery-empty-today">
                <div className="text-muted-foreground">Dnes zatím žádné fotky.</div>
                <Button variant="link" className="text-brand h-auto p-0" onClick={() => setDayF("all")} data-testid="show-all-photos">Zobrazit všechny</Button>
              </div>
            ) : <div className="p-8 text-center text-muted-foreground text-sm" data-testid="gallery-empty">Žádné fotky odpovídající filtru.</div>}
          </div>
          {!mobile && (
            <div className="hidden lg:block space-y-4">
              {canEye && mode === "redate" && (
                <div ref={redateRef}>
                  <RedatePanel date={redateDate} onDate={setRedateDate} selectedCount={selected.size} hiddenCount={hiddenSelected} dragCount={dragCount}
                    onApply={() => redate([...selected], redateDate)}
                    onDropId={(id) => {
                      setDragCount(0);
                      // Tažená fotka z výběru = přesouvá se celý výběr; jinak jen ona.
                      const ids = selected.has(id) ? [...selected] : [id];
                      redate(ids, redateDate || new Date().toISOString().slice(0, 10));
                    }} />
                </div>
              )}
              <div className="border border-border rounded-[16px] p-3">{detailNode}</div>
            </div>
          )}
        </div>
      )}

      {mobile && mode === "redate" && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border px-3 pt-3 flex items-center gap-2"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }} data-testid="redate-mobile-bar">
          <Input type="date" value={redateDate} onChange={(e) => setRedateDate(e.target.value)} className="h-11 min-w-0 flex-1" data-testid="redate-mobile-date" />
          <Button className="h-11 rounded-full shrink-0" disabled={!redateDate || selected.size === 0}
            onClick={() => redate([...selected], redateDate)} data-testid="redate-mobile-apply">
            Přesunout ({selected.size}){redateDate ? ` na ${formatDate(redateDate)}` : ""}
          </Button>
        </div>
      )}

      {mobile && detail && (
        <BottomSheet open={!!detail} onOpenChange={(o) => !o && setDetailId(null)} title="Fotka" testid="gallery-detail-sheet">
          {detailNode}
        </BottomSheet>
      )}

      <AlertDialog open={bulkDelete} onOpenChange={setBulkDelete}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Smazat {plur(selected.size, "fotku", "fotky", "fotek")}?</AlertDialogTitle>
            <AlertDialogDescription>Zmizí z banky i z deníku. Nejde to vrátit.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">Zpět</AlertDialogCancel>
            <AlertDialogAction data-testid="bulk-delete-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={doBulkDelete}>Smazat</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 187: úkol ze závad — jedna zakázka na úkol */}
      <Dialog open={!!defTask} onOpenChange={(o) => !o && setDefTask(null)}>
        <DialogContent sheet className="max-w-lg max-md:pt-8 overflow-y-auto md:max-h-[92vh]" onOpenAutoFocus={(e) => e.preventDefault()} data-testid="gallery-defect-task-dialog">
          <DialogHeader className="sr-only"><DialogTitle>Nový úkol ze závad</DialogTitle></DialogHeader>
          {defTask && (
            <DefectTaskForm drafts={drafts} setDrafts={setDrafts} projectId={defTask.projectId} projectName={defTask.projectName}
              assignee={defTask.assignee} sourceKind="gallery" sourceId={null} returnTask={defTask.returnTask}
              onEditDraft={(k) => { const p = photos.find((x) => x.url === drafts[k]?.photo_url); setDefTask(null); if (p) { setFocusDraft(k); setLightboxId(p.id); } }}
              onCreated={() => setDefTask(null)} bare />
          )}
        </DialogContent>
      </Dialog>
      {drafts.length > 0 && !lightbox && !defTask && (
        <button type="button" onClick={() => openDefTask()} data-testid="gallery-defects-chip"
          className="fixed z-40 left-4 right-4 md:left-auto md:right-6 bottom-[calc(var(--bottom-nav-h,5.5rem)+8px+env(safe-area-inset-bottom))] md:bottom-6 h-12 px-5 rounded-full bg-brand text-white font-extrabold shadow-lg">
          Závady · {drafts.length} → vytvořit úkol
        </button>
      )}

      {/* 185: jednotný prohlížeč fotek (stejný jako v deníku, docházce a úkolech) */}
      <PhotoPinViewer open={!!lightbox} onClose={() => { setLightboxId(null); setFocusDraft(null); }}
        photos={photos.map((p) => p.url)} start={Math.max(0, lbIndex)} focusDraft={focusDraft}
        editable={canLeadTasks(user)} drafts={drafts} setDrafts={setDrafts} onDone={() => openDefTask()}
        title={lightbox ? `${lightbox.project_name || "—"} · ${formatDate(lightbox.date)}` : ""}
        info={(url) => photoInfo(byUrl(url))}
        diary={canEye ? { isIn: (url) => photos.find((p) => p.url === url)?.status === "approved",
          toggle: (url) => { const p = photos.find((x) => x.url === url); if (p) eyeOne(p); } } : null}
        onShare={canEye ? (url) => { const p = photos.find((x) => x.url === url); if (p) shareToWhatsApp([p.id]); } : undefined}
        onDelete={canEye ? async (url) => {
          const p = photos.find((x) => x.url === url); if (!p) return;
          try { await api.delete(`/photos/${p.id}`); await refresh(); toast.success("Fotka smazána."); }
          catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        } : undefined} />

      {share && <ShareSheet ids={share} onClose={() => setShare(null)} />}

      <GalleryUploadDialog open={upload} onClose={() => setUpload(false)}
        defaultProject={st.project?.vals.length === 1 ? st.project.vals[0] : ""}
        defaultDate={effDate || ""} onDone={refresh} />
    </div>
  );
}
